import { useState, useEffect } from 'react'
import {
  Box, Button, Divider, IconButton, Stack, TextField, Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import MUIMarkdownEditor from '../../../Component/common/field/MUIMarkdownEditor'

/**
 * Inline release-note + documents editor for a single release.
 * Sits inside the ReleaseAccordionList accordion for each release.
 */
export default function ReleaseNoteSection({ release, releaseIdx, onChangeRelease }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(null)

  // Sync draft when external release data changes (e.g. after API load)
  useEffect(() => {
    setDraft({
      releaseNote: release?.releaseNote ?? '',
      documentList: release?.documentList ? [...release.documentList] : [],
    })
    setEditing(false)
  }, [release?.id]) // reset only when switching to a different release

  function onClickEdit() {
    setDraft({
      releaseNote: release?.releaseNote ?? '',
      documentList: release?.documentList ? [...release.documentList] : [],
    })
    setEditing(true)
  }

  function onClickCancel() {
    setDraft({
      releaseNote: release?.releaseNote ?? '',
      documentList: release?.documentList ? [...release.documentList] : [],
    })
    setEditing(false)
  }

  function onClickSave() {
    onChangeRelease(releaseIdx, {
      releaseNote: draft.releaseNote,
      documentList: draft.documentList,
    })
    setEditing(false)
  }

  function onChangeDoc(docIdx, field, value) {
    setDraft((prev) => ({
      ...prev,
      documentList: prev.documentList.map((doc, i) =>
        i === docIdx ? { ...doc, [field]: value } : doc
      ),
    }))
  }

  function onAddDoc() {
    setDraft((prev) => ({
      ...prev,
      documentList: [...prev.documentList, { name: '', url: '' }],
    }))
  }

  function onRemoveDoc(docIdx) {
    setDraft((prev) => ({
      ...prev,
      documentList: prev.documentList.filter((_, i) => i !== docIdx),
    }))
  }

  if (!draft) return null

  const isBeta = release?.status === 'BETA' || !release?.status

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />

      {/* ── Section header ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="h6">{"What's new? 🎉"}</Typography>
        {!editing ? (
          <IconButton size="small" onClick={onClickEdit} disabled={!isBeta} title={!isBeta ? 'Only BETA releases can be edited' : 'Edit'}>
            <EditIcon fontSize="small" />
          </IconButton>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={onClickSave}>
              Save
            </Button>
            <Button size="small" variant="outlined" startIcon={<CancelIcon />} onClick={onClickCancel}>
              Cancel
            </Button>
          </Stack>
        )}
      </Stack>

      {/* ── Release Note ── */}
      <MUIMarkdownEditor
        name="releaseNote"
        label="Release Note"
        value={draft.releaseNote}
        handleChange={(val) => setDraft((prev) => ({ ...prev, releaseNote: val }))}
        height={250}
        disabled={!editing}
      />

      {/* ── Documents ── */}
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>📄 Documents</Typography>
      {editing ? (
        <Stack spacing={1}>
          {draft.documentList.map((doc, docIdx) => (
            <Stack key={docIdx} direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                label="Name"
                value={doc.name}
                onChange={(e) => onChangeDoc(docIdx, 'name', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="URL"
                value={doc.url}
                onChange={(e) => onChangeDoc(docIdx, 'url', e.target.value)}
                sx={{ flex: 2 }}
              />
              <IconButton size="small" color="error" onClick={() => onRemoveDoc(docIdx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={onAddDoc} sx={{ alignSelf: 'flex-start' }}>
            Add Document
          </Button>
        </Stack>
      ) : (
        <Box>
          {release?.documentList?.length ? (
            release.documentList.map((doc, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                <OpenInNewIcon fontSize="small" color="action" />
                <a href={doc.url} target="_blank" rel="noopener noreferrer">{doc.name}</a>
              </Stack>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary"><em>No documents</em></Typography>
          )}
        </Box>
      )}
    </Box>
  )
}
